import Icon from '@ant-design/icons';
import { Select, Tabs, Tooltip } from 'antd';
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { useAtom } from 'jotai';
import { Concept } from 'components/TemplateEditor/Editor';
import { ReactComponent as GoogleSvg } from 'views/Conversation/assets/google.svg';
import { ReactComponent as GptSvg } from 'views/Conversation/assets/gpt.svg';
import { TranslateTool } from './TranslateTool';
import { getValueByType, mergePartialValue } from '../ExecuteContextsFieldV2';
import { DescendantExecuteContext } from '../share';
import { Result } from './Result';
import translateModeState from './translateModeState';

export interface ExtraTabsProps {
  platform?: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  value: DescendantExecuteContext;
  query?: DescendantExecuteContext;
  concepts?: Concept[];
  allowDividing?: boolean;
  recommendTemplates?: Marketing.Policy['recommendTemplates'];
  newRecommendTemplates?: Aide.RecommendTemplate[];
  onChange?: (value: DescendantExecuteContext) => void;
  result?: JSX.Element;
  systemContent?: string;
  supportTranslate?: boolean;
  supportPreview?: boolean;
  copyTip?: string;
}

export function ExtraTabs(props: ExtraTabsProps) {
  const {
    platform,
    region,
    value,
    query,
    concepts,
    allowDividing,
    recommendTemplates,
    newRecommendTemplates,
    onChange,
    result,
    systemContent,
    supportTranslate: supportTranslateProp = true,
    supportPreview = true,
    copyTip,
  } = props;

  const { t } = useTranslate();

  const { content: textValue } = getValueByType(value, 'text');

  const [activeTab, setActiveTab] = useState(supportPreview ? 'preview' : 'translate');

  const supportTranslate = supportTranslateProp && textValue.length > 0;

  const [translateMode, setTranslateMode] = useAtom(translateModeState);

  const extra = supportTranslate && activeTab === 'translate' && (
    <Select
      value={translateMode}
      onChange={setTranslateMode}
      options={[
        {
          value: 'GOOGLE',
          label: (
            <Tooltip placement="left" title={t('ViewsChat.使用x翻译', { x: 'Google' })}>
              <Icon component={GoogleSvg} style={{ width: '100%' }} />
            </Tooltip>
          ),
        },
        {
          value: 'CHAT_GPT',
          label: (
            <Tooltip placement="left" title={t('ViewsChat.使用x翻译', { x: 'ChatGPT' })}>
              <Icon component={GptSvg} style={{ width: '100%' }} />
            </Tooltip>
          ),
        },
      ]}
    />
  );

  return (
    <Tabs size="small" activeKey={activeTab} onChange={setActiveTab} tabBarExtraContent={extra}>
      {supportTranslate && (
        <Tabs.TabPane key="translate" tab={t('aide.翻译工具')}>
          <TranslateTool
            region={region}
            platform={platform}
            value={textValue}
            onChangeValue={
              onChange
                ? (content) =>
                    onChange(mergePartialValue(value, { content, translatedContent: [] }, 'text'))
                : undefined
            }
            concepts={concepts}
            allowDividing={allowDividing}
            recommendTemplates={recommendTemplates}
            newRecommendTemplates={newRecommendTemplates}
            translateMode={translateMode}
            copyTip={copyTip}
          />
        </Tabs.TabPane>
      )}
      {supportPreview && (
        <Tabs.TabPane key="preview" tab={t('aide.效果预览')}>
          {result || (
            <Result
              platform={platform}
              region={region}
              value={value}
              query={query}
              systemContent={systemContent}
            />
          )}
        </Tabs.TabPane>
      )}
    </Tabs>
  );
}
